import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Check } from 'lucide-react';
import Hyperlink from '../../Hyperlink';
import './ContentBanner.css';

/**
 * ContentBanner Component
 * Figma node: 3874:669 (Secondary Banner)
 *
 * Two-column layout: content block + visual block (image or slider).
 * On mobile the blocks stack vertically.
 * `direction` prop controls order (content first or image first).
 *
 * Visual can be:
 *  1. A single image (`image` prop)
 *  2. A slider with fade-in/out + slow zoom (`images` array prop)
 *
 * Reuses existing DS components: Hyperlink (editorial-large variant).
 *
 * Design tokens used:
 *  - Colors: --color-gray-1, --color-gray-7, --color-gray-10, --color-brand-primary-alternative
 *  - Spacing: --space-ui-small-s/m, --space-ui-medium-s/m/l, --space-ui-large-s/m
 *  - Layout: --layout-max-width, --layout-padding-mobile/desktop
 *  - Radius: --radius-card
 *  - Transitions: --transition-function-ui
 *  - Typography: typo-h5, typo-h2-editorial, typo-h4, typo-body, typo-body-sm
 */

const ContentBanner = ({
  /* Direction */
  direction,
  visualPosition = 'end',

  /* Text alignment inside the content block */
  align = 'left',

  /* Theme */
  theme = 'light',

  /* Subtitle */
  showSubtitle = true,
  subtitle = '',

  /* Title */
  showTitle = true,
  title = '',
  titleVariant = 'h2',
  titleAs,

  /* Bullets */
  showBullets = true,
  bullets = [],

  /* Paragraph */
  showParagraph = true,
  paragraph = '',

  /* Slogan */
  showSlogan = true,
  slogan = '',

  /* Link */
  showLink = true,
  linkLabel = '',
  linkHref = '#',
  onLinkClick,

  /* Caption */
  showCaption = true,
  caption = '',

  /* Content background color (CSS value, e.g. var(--color-gray-10)) */
  contentBg = null,

  /* Visual — single image */
  image = null,
  imageAlt = '',

  /* Visual — slider */
  images = null,
  sliderInterval = 5,

  /* Vertical padding overrides */
  paddingTop = null,
  paddingBottom = null,

  /* Extra */
  className = '',
}) => {
  /* =========================================================================
   * Slider state
   * ======================================================================= */
  const [activeSlide, setActiveSlide] = useState(0);
  const timerRef = useRef(null);

  const isSlider = Array.isArray(images) && images.length > 1;

  const startTimer = useCallback(() => {
    if (!isSlider) return;
    timerRef.current = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % images.length);
    }, sliderInterval * 1000);
  }, [isSlider, images, sliderInterval]);

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [startTimer]);

  /* =========================================================================
   * Determine visual source
   * ======================================================================= */
  const hasSingleImage = !isSlider && (image || (Array.isArray(images) && images.length === 1));
  const singleSrc = image || (Array.isArray(images) && images.length === 1 ? images[0].src || images[0] : null);
  const singleAlt = imageAlt || (Array.isArray(images) && images.length === 1 ? images[0].alt || '' : '');

  /* =========================================================================
   * Root classes
   * ======================================================================= */
  const rootClasses = [
    'content-banner',
    (visualPosition === 'start' || direction === 'right') ? 'content-banner--reverse' : '',
    align !== 'left' ? `content-banner--align-${align}` : '',
    theme === 'dark' ? 'content-banner--dark' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  /* =========================================================================
   * Render helpers
   * ======================================================================= */
  const renderBullets = () => {
    if (!showBullets || !bullets.length) return null;
    return (
      <div className="content-banner__bullets">
        {bullets.map((bullet, idx) => (
          <div className="content-banner__bullet" key={idx}>
            <span className="content-banner__bullet-icon">
              <Check />
            </span>
            <div className="content-banner__bullet-copy">
              {bullet.title && (
                <p className="typo-h5 content-banner__bullet-title">
                  {bullet.title}
                </p>
              )}
              {bullet.description && (
                <p className="typo-body content-banner__bullet-description">
                  {bullet.description}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderVisual = () => {
    if (isSlider) {
      return (
        <div
          className="content-banner__slider"
          style={{ '--content-banner-interval': `${sliderInterval}s` }}
        >
          {images.map((img, idx) => {
            const src = typeof img === 'string' ? img : img.src;
            const alt = typeof img === 'string' ? '' : img.alt || '';
            return (
              <div
                className={`content-banner__slide${idx === activeSlide ? ' content-banner__slide--active' : ''}`}
                key={idx}
              >
                <img src={src} alt={alt} />
              </div>
            );
          })}
        </div>
      );
    }

    if (hasSingleImage && singleSrc) {
      return (
        <img
          className="content-banner__image"
          src={singleSrc}
          alt={singleAlt}
        />
      );
    }

    return null;
  };

  /* =========================================================================
   * Render
   * ======================================================================= */
  const rootStyle = {
    ...(paddingTop != null && { '--content-banner-pt': paddingTop }),
    ...(paddingBottom != null && { '--content-banner-pb': paddingBottom }),
  };

  // The tag follows the size unless the page says otherwise: a banner in the middle of a page
  // keeps the h1 look with titleAs="h2", so the page has one h1 (its hero).
  const TitleTag = titleAs || (titleVariant === 'h1' ? 'h1' : 'h2');

  return (
    <section className={rootClasses} style={Object.keys(rootStyle).length ? rootStyle : undefined}>
      <div className="content-banner__container">
        {/* Content block */}
        <div
          className="content-banner__content"
          style={contentBg ? { '--content-banner-content-bg': contentBg } : undefined}
        >
          <div className="content-banner__content-inner">
            {showSubtitle && subtitle && (
              <div className="content-banner__subtitle-row">
                <p className="typo-h5 content-banner__subtitle">{subtitle}</p>
              </div>
            )}

            {showTitle && title && (
              <div className="content-banner__title-row">
                <TitleTag className={`typo-${titleVariant === 'h1' ? 'h1' : 'h2'}-editorial content-banner__title`}>
                  {title}
                </TitleTag>
              </div>
            )}

            {renderBullets()}

            {showParagraph && paragraph && (
              <div className="content-banner__paragraph-row">
                <p className="typo-body content-banner__paragraph">
                  {paragraph}
                </p>
              </div>
            )}

            {showSlogan && slogan && (
              <div className="content-banner__slogan-row">
                <p className="typo-h4 content-banner__slogan">{slogan}</p>
              </div>
            )}

            {showLink && linkLabel && (
              <div className="content-banner__link-row">
                <Hyperlink
                  href={linkHref}
                  variant="editorial-large"
                  showIconRight
                  onClick={onLinkClick}
                >
                  {linkLabel}
                </Hyperlink>
              </div>
            )}
          </div>

          {showCaption && caption && (
            <div className="content-banner__caption-row">
              <p className="typo-body-sm content-banner__caption">
                {caption}
              </p>
            </div>
          )}
        </div>

        {/* Visual block */}
        <div className="content-banner__visual">{renderVisual()}</div>
      </div>
    </section>
  );
};

/* ===========================================================================
 * PropTypes
 * ========================================================================= */
ContentBanner.propTypes = {
  /** Side of the visual on desktop: 'end' (right, default) or 'start' (left) — the DS name shared with ContentBannerV2 / HeroBannerV3 */
  visualPosition: PropTypes.oneOf(['end', 'start']),
  /** @deprecated Use `visualPosition` ('right' = visual first = `visualPosition="start"`). */
  direction: PropTypes.oneOf(['left', 'right']),

  /** Text alignment inside the content block */
  align: PropTypes.oneOf(['left', 'center', 'right']),

  /** Color theme */
  theme: PropTypes.oneOf(['light', 'dark']),

  showSubtitle: PropTypes.bool,
  subtitle: PropTypes.string,

  showTitle: PropTypes.bool,
  title: PropTypes.string,

  /** Title typography variant */
  titleVariant: PropTypes.oneOf(['h1', 'h2']),
  /** Heading element of the title (default: follows titleVariant) — e.g. "h2" for an h1-sized banner below the page's hero */
  titleAs: PropTypes.oneOf(['h1', 'h2', 'h3']),

  /** Array of bullet items: { title: string, description: string } */
  showBullets: PropTypes.bool,
  bullets: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
    }),
  ),

  showParagraph: PropTypes.bool,
  paragraph: PropTypes.string,

  showSlogan: PropTypes.bool,
  slogan: PropTypes.string,

  showLink: PropTypes.bool,
  linkLabel: PropTypes.string,
  linkHref: PropTypes.string,
  onLinkClick: PropTypes.func,

  showCaption: PropTypes.bool,
  caption: PropTypes.string,

  /** Override padding-top (any CSS value, e.g. 'var(--space-ui-large-s)' or '0px') */
  paddingTop: PropTypes.string,

  /** Override padding-bottom (any CSS value) */
  paddingBottom: PropTypes.string,

  /** Custom background color for the content block (any CSS color value) */
  contentBg: PropTypes.string,

  /** Single image source */
  image: PropTypes.string,
  imageAlt: PropTypes.string,

  /** Array of images for slider mode — string[] or { src, alt }[] */
  images: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ src: PropTypes.string, alt: PropTypes.string }),
    ]),
  ),

  /** Seconds between slides (also controls zoom duration) */
  sliderInterval: PropTypes.number,

  className: PropTypes.string,
};

export default ContentBanner;
